// =============================================================================
// domain/forYouToday.ts — the "FOR YOU TODAY" card's two findings.
// -----------------------------------------------------------------------------
// Ported from `marketIntelPersonalNote` in the old client. It renders at the top
// of the Pre-Market panel, above the carried-forward focus:
//
//   Your strongest edge is VA REVERSION — 50% over 18 trades (+$79/trade). Favor it.
//   Your costliest leak is chased — 17 trades, -$400. Watch for it today.
//
// The old function also emits a third line about the day's highest-impact
// calendar event — but ONLY when it is handed a macro brief, and BOTH journal
// call sites invoke it as `marketIntelPersonalNote()` with no argument. On this
// tab the event line is unreachable, so it is not ported here. It belongs to the
// Macro Brief tab, which is a separate port.
//
// ── 🚨 THIS IS NOT `patternLeaderboard`, AND MUST NOT BE REWRITTEN AS IT ──
//
// Both group trades by pattern and both compute a win rate and an expectancy.
// They disagree on BOTH denominators, and the old client computes each one the
// way this file does:
//
//                  patternLeaderboard          forYouToday
//   win rate       wins / (wins + losses)      wins / sample
//   expectancy     total / trades WITH a       total / sample
//                  value in the display unit
//
// A scratch therefore lowers the number here and does not there. Folding this
// into the leaderboard "because it is the same statistic" would move the printed
// figures away from what beta shows on the same book — silently, since every
// leaderboard test would stay green. That is the recurring defect in this port:
// an assertion that is true, of a case that cannot expose the difference.
//
// The gates (8 trades to say anything, 4 per pattern, 3 per tag) come from the
// old client verbatim. They are the difference between a coaching claim and
// noise dressed as one, so they are constants here rather than parameters — a
// caller that could lower them would eventually lower them.
// =============================================================================

/** What a finding needs from a trade. Deliberately narrow, like the leaderboard's. */
export interface CoachingTrade {
  patternId?: string | null;
  netPnl?: number | null;
  mistakeTags?: readonly string[] | null;
}

export interface EdgeFinding {
  patternId: string;
  /** Resolved through the catalogue; `null` when the id no longer resolves. */
  name: string | null;
  sample: number;
  wins: number;
  /** Percent, 0–100, ALREADY ROUNDED — the old client rounds before printing. */
  winPct: number;
  /** Mean net P&L per trade, over the whole sample. */
  expectancy: number;
}

export interface LeakFinding {
  tag: string;
  sample: number;
  /** Cumulative net P&L across every trade carrying the tag. Always negative. */
  totalPnl: number;
}

export interface ForYouToday {
  edge: EdgeFinding | null;
  leak: LeakFinding | null;
}

/** Trades below which the card says nothing at all. */
export const MIN_TRADES = 8;
/** A pattern needs this many before it can be called an edge. */
export const MIN_PATTERN_TRADES = 4;
/** A mistake tag needs this many before it can be called a leak. */
export const MIN_TAG_TRADES = 3;

/**
 * ⚠ `Number(v) || 0`, matching the old client's `t.core?.netPnl || 0` exactly.
 *
 * Null, undefined and NaN all become 0 — so an unpriced trade is counted in the
 * sample as a non-win worth nothing, which drags expectancy toward zero. That is
 * beta's behaviour and the numbers on screen have to agree with it. The
 * leaderboard makes the opposite choice (skip, do not zero) for its own totals,
 * which is the second half of why these two cannot share an implementation.
 */
const pnlOf = (t: CoachingTrade): number => Number(t.netPnl) || 0;

/**
 * The two findings, over whatever population the caller hands in.
 *
 * ⚠ The population is the WHOLE BOOK for the selected accounts, not a date
 * window — "your strongest edge" is a claim about the trader, and answering it
 * from the last 45 days would name a different pattern with different figures
 * while reading like an all-time statement. `selectors/forYouToday.ts` is where
 * that scope is enforced.
 */
export function forYouToday(
  trades: readonly CoachingTrade[],
  namePattern: (id: string) => string | null,
): ForYouToday {
  // ⚠ The whole card, not just one line. Under 8 trades neither finding is worth
  // making, and half a card is worse than none: "your costliest leak" with no
  // edge beside it reads as a verdict on a book too small to have one.
  if (trades.length < MIN_TRADES) return { edge: null, leak: null };

  return { edge: strongestEdge(trades, namePattern), leak: costliestLeak(trades) };
}

function strongestEdge(
  trades: readonly CoachingTrade[],
  namePattern: (id: string) => string | null,
): EdgeFinding | null {
  const byPattern = new Map<string, { n: number; pnl: number; w: number }>();
  for (const t of trades) {
    const pid = t.patternId ? String(t.patternId) : '';
    // ⚠ Untagged trades are DROPPED here, unlike the leaderboard where they get
    // their own row. "Your strongest edge is (Untagged)" is not a finding.
    if (!pid) continue;
    let s = byPattern.get(pid);
    if (!s) byPattern.set(pid, (s = { n: 0, pnl: 0, w: 0 }));
    const p = pnlOf(t);
    s.n++;
    s.pnl += p;
    if (p > 0) s.w++;
  }

  let best: EdgeFinding | null = null;
  // ⚠ Map iteration is INSERTION order, and the comparison below is strict, so a
  // tie is won by whichever pattern appears first in `trades`. The old client
  // relies on the same thing via object key order; matching it keeps two clients
  // from naming different patterns on an exactly tied book.
  for (const [pid, s] of byPattern) {
    if (s.n < MIN_PATTERN_TRADES) continue;
    const expectancy = s.pnl / s.n;
    if (best && expectancy <= best.expectancy) continue;
    best = {
      patternId: pid,
      name: namePattern(pid),
      sample: s.n,
      wins: s.w,
      winPct: Math.round((s.w / s.n) * 100),
      expectancy,
    };
  }

  // ⚠ MUST BE GREEN. The best of a book of losing setups is still a losing setup,
  // and "your strongest edge" printed over a negative expectancy is advice to
  // keep doing the thing that is costing money.
  return best && best.expectancy > 0 ? best : null;
}

function costliestLeak(trades: readonly CoachingTrade[]): LeakFinding | null {
  const byTag = new Map<string, { n: number; pnl: number }>();
  for (const t of trades) {
    // ⚠ NOT de-duplicated within a trade, matching the old client. A trade
    // carrying the same tag twice counts twice. It should not happen — the
    // annotation editor is a toggle list — and de-duplicating here would make
    // the two clients disagree on any row where it did.
    for (const raw of t.mistakeTags ?? []) {
      const tag = String(raw ?? '');
      if (!tag) continue;
      let s = byTag.get(tag);
      if (!s) byTag.set(tag, (s = { n: 0, pnl: 0 }));
      s.n++;
      s.pnl += pnlOf(t);
    }
  }

  let worst: LeakFinding | null = null;
  for (const [tag, s] of byTag) {
    if (s.n < MIN_TAG_TRADES) continue;
    if (worst && s.pnl >= worst.totalPnl) continue;
    worst = { tag, sample: s.n, totalPnl: s.pnl };
  }

  // ⚠ Must actually be COSTING money. A tag with a positive total is a habit the
  // trader has flagged as a mistake and is getting away with; naming it "your
  // costliest leak" alongside a green number is incoherent.
  return worst && worst.totalPnl < 0 ? worst : null;
}
